/**
 * ClubSettingsPage - Comprehensive Club Management
 * 
 * Owner-only page for managing all club settings including details, location,
 * images, members, roles, and dangerous actions (ownership transfer, deletion).
 * 
 * NOTE: Supersedes legacy ClubEditPage.tsx (deleted December 2025)
 * Field parity confirmed: name, description, city, state, hero_image_url, avatar_image_url
 * 
 * If backend adds new club fields, update this page to maintain parity.
 * Reference: docs/design/FIGMA_IMPLEMENTATION_PLAN.md - Phase 7C
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useClub, useUpdateClub, useDeleteClub, useTransferOwnership } from '../hooks/useClubs';
import { useClubMembers, useRemoveMember, useUpdateMemberRole } from '../hooks/useMembers';
import { useAuth } from '../hooks/useAuth';
import { TopBar, Button, Input, RoleBadge, EmptyState, Card } from '../components';
import { HeroImageUpload } from '../components/HeroImageUpload';
import { clubsApi } from '../lib/api/clubs';
import { AlertTriangle, UserX, UserCog, Trash2, Camera, MapPin } from 'lucide-react';
import { createClubSettingsMobileMenu } from '../components/MobileContextMenu';
import { US_STATES } from '../constants/usStates';

export default function ClubSettingsPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  // React Query hooks
  const { data: clubData, isLoading: clubLoading, error: clubError } = useClub(slug || '');
  const club = clubData?.club;
  const membership = clubData?.membership;
  const isOwner = membership?.is_owner || false;
  
  const { data: membersData, isLoading: membersLoading } = useClubMembers(slug || '');
  const members = membersData || [];
  
  const updateClubMutation = useUpdateClub(slug || '');
  const deleteClubMutation = useDeleteClub();
  const transferOwnershipMutation = useTransferOwnership(club?.id || '', slug || '');
  const removeMemberMutation = useRemoveMember(slug || '');
  const updateMemberRoleMutation = useUpdateMemberRole(slug || '');
  
  // Edit state - initialize from club data
  const [editMode, setEditMode] = useState(false);
  const [editName, setEditName] = useState(club?.name || '');
  const [editDescription, setEditDescription] = useState(club?.description || '');
  const [editCity, setEditCity] = useState(club?.city || '');
  const [editState, setEditState] = useState(club?.state || '');

  
  // Confirmation dialogs
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [selectedNewOwner, setSelectedNewOwner] = useState('');
  const [memberToRemove, setMemberToRemove] = useState<string | null>(null);
  
  // Role management
  const [memberToChangeRole, setMemberToChangeRole] = useState<string | null>(null);
  const [newRole, setNewRole] = useState<'leader' | 'moderator' | 'member'>('member');
  
  // Success message and avatar upload state
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [isAvatarUploading, setIsAvatarUploading] = useState(false);



  // Handle club info update
  const handleSave = async () => {
    if (!club) return;
    
    try {
      await updateClubMutation.mutateAsync({
        name: editName.trim(),
        description: editDescription.trim() || undefined,
        city: editCity.trim() || undefined,
        state: editState || undefined
      });
      
      setSuccessMessage('Club settings updated successfully');
      setEditMode(false);
    } catch (err) {
      // Error handled by React Query
      console.error('Failed to update club:', err);
    }
  };

  // Handle member removal
  const handleRemoveMember = async (memberId: string) => {
    try {
      await removeMemberMutation.mutateAsync(memberId);
      setSuccessMessage('Member removed successfully');
      setMemberToRemove(null);
    } catch (err) {
      console.error('Failed to remove member:', err);
    }
  };

  // Handle role change
  const handleChangeRole = async (memberId: string, role: 'leader' | 'moderator' | 'member') => {
    try {
      await updateMemberRoleMutation.mutateAsync({ userId: memberId, role: role as 'leader' | 'member' });
      setSuccessMessage(`Member role updated to ${role}`);
      setMemberToChangeRole(null);
    } catch (err) {
      console.error('Failed to update member role:', err);
    }
  };

  // Handle ownership transfer
  const handleTransferOwnership = async () => {
    if (!selectedNewOwner) return;
    
    try {
      await transferOwnershipMutation.mutateAsync(selectedNewOwner);
      setSuccessMessage('Ownership transferred successfully');
      setShowTransferDialog(false);
      setSelectedNewOwner('');
      
      // Navigate away since user is no longer owner
      setTimeout(() => navigate(`/clubs/${slug}`), 2000);
    } catch (err) {
      console.error('Failed to transfer ownership:', err);
    }
  };

  // Handle club deletion
  const handleDeleteClub = async () => {
    if (!slug) return;
    
    try {
      await deleteClubMutation.mutateAsync(slug);
      setSuccessMessage('Club deleted successfully');
      setShowDeleteConfirm(false);
      
      // Navigate to clubs list
      setTimeout(() => navigate('/clubs'), 2000);
    } catch (err) {
      console.error('Failed to delete club:', err);
    }
  };

  // Clear messages after delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Create mobile menu configuration
  const clubSettingsMenu = slug ? createClubSettingsMobileMenu(
    navigate,
    logout,
    slug,
    isOwner,
    setShowDeleteConfirm
  ) : undefined;

  if (clubLoading || membersLoading) {
    return (
      <div className="flex flex-col bg-oro-bg">
        <TopBar 
          title="Settings" 
          onBack={() => navigate(`/clubs/${slug}`)}
          mobileMenu={clubSettingsMenu}
        />
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-12 text-center">
            <p className="text-text-muted">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  if (clubError || !isOwner) {
    return (
      <div className="flex flex-col bg-oro-bg">
        <TopBar 
          title="Settings" 
          onBack={() => navigate(`/clubs/${slug}`)}
          mobileMenu={clubSettingsMenu}
        />
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-12">
            <EmptyState
              icon="⚠️"
              title="Unable to load club"
              message={clubError?.message || 'Only club owners can access settings'}
              actionLabel="Back to Club"
              onAction={() => navigate(`/clubs/${slug}`)}
            />
          </div>
        </div>
      </div>
    );
  }

  if (!club) return null;

  const adminMembers = members.filter(m => m.role === 'leader' && m.user_id !== club.owner_id);
  const canTransferOwnership = adminMembers.length > 0;

  const anyError = updateClubMutation.error || 
    deleteClubMutation.error || 
    transferOwnershipMutation.error || 
    removeMemberMutation.error || 
    updateMemberRoleMutation.error;

  return (
    <div className="flex flex-col bg-oro-bg">
      <TopBar 
        title="Club Settings" 
        onBack={() => navigate(`/clubs/${slug}`)}
        mobileMenu={clubSettingsMenu}
      />
      
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-6 space-y-6">
          {/* Success Message */}
          {successMessage && (
            <div className="rounded-lg border border-green-500/40 bg-green-500/10 p-4">
              <p className="text-sm text-green-200">{successMessage}</p>
            </div>
          )}
          
          {/* Error Message */}
          {anyError && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-4">
              <p className="text-sm text-red-200">{(anyError as Error).message || 'An error occurred'}</p>
            </div>
          )}

          {/* Club Details Section */}
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-text-primary">Club Details</h2>
              <Button 
                variant={editMode ? 'ghost' : 'secondary'}
                size="sm"
                onClick={() => {
                  if (editMode) {
                    setEditName(club.name);
                    setEditDescription(club.description || '');
                    setEditCity(club.city || '');
                    setEditState(club.state || '');
                  }
                  setEditMode(!editMode);
                }}
              >
                {editMode ? 'Cancel' : 'Edit'}
              </Button>
            </div>
            
            <div className="bg-oro-surface border border-oro-border rounded-2xl p-4 space-y-4">
              {/* Club Name */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Club Name
                </label>
                {editMode ? (
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Club name"
                    maxLength={100}
                    required
                  />
                ) : (
                  <p className="text-text-primary">{club.name}</p>
                )}
              </div>
              
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Description
                </label>
                {editMode ? (
                  <textarea
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="Club description"
                    maxLength={500}
                    rows={3}
                    className="w-full px-4 py-3 rounded-lg border border-oro-border bg-oro-surface-alt text-text-primary placeholder:text-text-muted focus:border-accent focus:ring-2 focus:ring-accent/20 disabled:opacity-50 resize-none transition-colors"
                  />
                ) : (
                  <p className="text-text-primary">
                    {club.description || 'No description set'}
                  </p>
                )}
              </div>
              
              {/* Location */}
              {editMode && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-text-secondary">
                    <MapPin className="w-4 h-4" />
                    <label className="block text-sm font-medium">Location</label>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Input
                        value={editCity}
                        onChange={(e) => setEditCity(e.target.value)}
                        placeholder="City"
                      />
                    </div>
                    <div>
                      <select
                        value={editState}
                        onChange={(e) => setEditState(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg border border-oro-border bg-oro-surface-alt text-text-primary focus:border-accent focus:ring-2 focus:ring-accent/20 transition-colors"
                      >
                        <option value="">State</option>
                        {US_STATES.map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
              
              {!editMode && (club.city || club.state) && (
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Location
                  </label>
                  <p className="text-text-primary flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-accent" />
                    {[club.city, club.state].filter(Boolean).join(', ')}
                  </p>
                </div>
              )}
              
              {/* Save Button */}
              {editMode && (
                <Button
                  variant="primary"
                  onClick={handleSave}
                  loading={updateClubMutation.isPending}
                  disabled={!editName.trim()}
                  className="w-full"
                >
                  Save Changes
                </Button>
              )}
            </div>
          </section>

          {/* Hero Image Section */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-text-primary">Cover Photo</h2>
            
            <div className="bg-oro-surface border border-oro-border rounded-2xl p-4">
              <p className="text-sm text-text-secondary mb-4">
                Upload a cover photo for your club. Recommended size: 1200×400px.
              </p>
              
              <HeroImageUpload
                currentImageUrl={club.hero_image_url}
                onImageChange={() => {
                  // React Query will automatically update the cache when we refetch
                }}
                onImageUpload={async (file) => {
                  try {
                    const response = await clubsApi.uploadClubHeroImage(club.id, file);
                    setSuccessMessage('Cover photo updated successfully');
                    // Trigger a refetch of club data to get the updated hero_image_url
                    window.location.reload(); // Simple solution for now
                    return response.image_url;
                  } catch (error) {
                    throw new Error(error instanceof Error ? error.message : 'Failed to upload cover photo');
                  }
                }}
                onImageRemove={async () => {
                  try {
                    await clubsApi.removeClubHeroImage(club.id);
                    setSuccessMessage('Cover photo removed successfully');
                    // Trigger a refetch of club data to reflect the removal
                    window.location.reload(); // Simple solution for now
                  } catch (error) {
                    throw new Error(error instanceof Error ? error.message : 'Failed to remove cover photo');
                  }
                }}
                enableCrop={true}
                className="w-full"
              />
            </div>
          </section>

          {/* Club Avatar Section - Issue #562 */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-text-primary">Avatar</h2>
            
            <div className="bg-oro-surface border border-oro-border rounded-2xl p-4">
              <p className="text-sm text-text-secondary mb-4">
                Square profile image shown in club lists. Falls back to cover photo if not set. Recommended size: 200×200px.
              </p>
              
              {/* Avatar with Camera Button */}
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full bg-oro-surface-alt flex items-center justify-center text-3xl font-bold text-text-primary ring-2 ring-oro-border overflow-hidden">
                    {club.avatar_image_url || club.hero_image_url ? (
                      <img 
                        src={(club.avatar_image_url || club.hero_image_url) as string} 
                        alt={club.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      club.name.charAt(0).toUpperCase()
                    )}
                  </div>
                  <button 
                    onClick={() => {
                      const input = document.getElementById('club-avatar-input') as HTMLInputElement;
                      input?.click();
                    }}
                    disabled={isAvatarUploading}
                    className={`absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-oro-bg border border-oro-surface flex items-center justify-center transition-colors ${
                      isAvatarUploading 
                        ? 'opacity-50 cursor-not-allowed' 
                        : 'hover:bg-oro-surface-alt'
                    }`}
                  >
                    <Camera className={`w-4 h-4 text-text-primary ${isAvatarUploading ? 'animate-pulse' : ''}`} />
                  </button>
                  <input
                    id="club-avatar-input"
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      
                      setIsAvatarUploading(true);
                      try {
                        const formData = new FormData();
                        formData.append('image', file);
                        
                        const response = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/upload/club-avatar/${club.id}`, {
                          method: 'POST',
                          body: formData,
                          credentials: 'include'
                        });

                        if (!response.ok) {
                          const error = await response.json();
                          throw new Error(error.message || 'Upload failed');
                        }

                        setSuccessMessage('Club avatar updated successfully');
                        window.location.reload();
                      } catch (error) {
                        setAvatarError(error instanceof Error ? error.message : 'Failed to upload avatar');
                      } finally {
                        setIsAvatarUploading(false);
                        e.target.value = '';
                      }
                    }}
                    className="hidden"
                  />
                </div>
                
                {/* Remove Avatar Button (if avatar exists) */}
                {club.avatar_image_url && (
                  <button
                    onClick={async () => {
                      if (!confirm('Remove club avatar? It will fall back to the cover photo.')) return;
                      
                      setIsAvatarUploading(true);
                      try {
                        const response = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/upload/club-avatar/${club.id}`, {
                          method: 'DELETE',
                          credentials: 'include'
                        });

                        if (!response.ok) {
                          const error = await response.json();
                          throw new Error(error.message || 'Removal failed');
                        }

                        setSuccessMessage('Club avatar removed successfully');
                        window.location.reload();
                      } catch (error) {
                        setAvatarError(error instanceof Error ? error.message : 'Failed to remove avatar');
                      } finally {
                        setIsAvatarUploading(false);
                      }
                    }}
                    disabled={isAvatarUploading}
                    className="text-sm text-text-secondary hover:text-oro-error transition-colors"
                  >
                    Remove Avatar
                  </button>
                )}
              </div>
              
              {/* Avatar Error Message */}
              {avatarError && (
                <div className="mt-2 p-3 bg-oro-error/10 border border-oro-error/20 rounded-lg text-sm text-oro-error">
                  {avatarError}
                </div>
              )}
            </div>
          </section>

          {/* Members Section */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-text-primary">
              Members ({members.length})
            </h2>
            
            <div className="space-y-3">
              {members.map((member) => (
                <div
                  key={member.user_id}
                  className="bg-oro-surface border border-oro-border rounded-2xl p-4 flex items-center justify-between"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-text-primary truncate">
                        {member.public_display_name}
                      </h3>
                      {member.user_id === club.owner_id && (
                        <span className="px-2 py-0.5 text-xs bg-accent/20 text-accent rounded-full font-medium">
                          Owner
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <RoleBadge role={member.role} size="xs" />
                      <span className="text-sm text-text-muted truncate">{member.email}</span>
                    </div>
                  </div>
                  
                  {member.user_id !== club.owner_id && (
                    <div className="flex items-center gap-2 ml-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setMemberToChangeRole(member.user_id);
                          setNewRole(member.role as 'leader' | 'moderator' | 'member');
                        }}
                      >
                        <UserCog className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setMemberToRemove(member.user_id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <UserX className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Danger Zone Section */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Danger Zone
            </h2>
            
            <div className="space-y-4">
              {/* Transfer Ownership */}
              <div className="bg-oro-surface border border-amber-500/30 rounded-2xl p-4">
                <h3 className="font-medium text-amber-200 mb-2">Transfer Ownership</h3>
                <p className="text-sm text-text-secondary mb-4">
                  Transfer club ownership to another leader. You will lose owner privileges.
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowTransferDialog(true)}
                  disabled={!canTransferOwnership}
                  className="border-amber-500/40 text-amber-200"
                >
                  Transfer Ownership
                </Button>
                {!canTransferOwnership && (
                  <p className="text-xs text-text-muted mt-2">
                    Promote a member to admin first
                  </p>
                )}
              </div>
              
              {/* Delete Club */}
              <div className="bg-oro-surface border border-red-500/30 rounded-2xl p-4">
                <h3 className="font-medium text-red-200 mb-2 flex items-center gap-2">
                  <Trash2 className="w-4 h-4" />
                  Delete Club
                </h3>
                <p className="text-sm text-text-secondary mb-4">
                  Permanently delete this club. All data will be lost forever.
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="border-red-500/40 text-red-200"
                >
                  Delete Club
                </Button>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Confirmation Dialogs */}
      
      {/* Remove Member Confirmation */}
      {memberToRemove && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <Card className="p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Remove Member</h3>
            <p className="text-text-secondary mb-6">
              Are you sure you want to remove {members.find(m => m.user_id === memberToRemove)?.public_display_name}? 
              They will lose access to the club immediately.
            </p>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setMemberToRemove(null)}
                disabled={removeMemberMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => handleRemoveMember(memberToRemove)}
                loading={removeMemberMutation.isPending}
                className="bg-red-600 hover:bg-red-700"
              >
                Remove Member
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Change Role Dialog */}
      {memberToChangeRole && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <Card className="p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Change Member Role</h3>
            <p className="text-text-secondary mb-4">
              Update role for {members.find(m => m.user_id === memberToChangeRole)?.public_display_name}:
            </p>
            
            <div className="space-y-2 mb-6">
              {(['member', 'moderator', 'leader'] as const).map((role) => (
                <label key={role} className="flex items-center gap-3 p-3 rounded-lg border border-oro-border hover:bg-oro-surface-alt/30 cursor-pointer">
                  <input
                    type="radio"
                    name="role"
                    value={role}
                    checked={newRole === role}
                    onChange={(e) => setNewRole(e.target.value as 'leader' | 'moderator' | 'member')}
                    className="text-accent"
                  />
                  <div className="flex items-center gap-2">
                    <RoleBadge role={role} size="xs" />
                    <div>
                      <p className="font-medium capitalize">{role}</p>
                      <p className="text-xs text-text-muted">
                        {role === 'leader' && 'Full club management access'}
                        {role === 'moderator' && 'Event management and moderation'}
                        {role === 'member' && 'Basic club access'}
                      </p>
                    </div>
                  </div>
                </label>
              ))}
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setMemberToChangeRole(null)}
                disabled={updateMemberRoleMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => memberToChangeRole && handleChangeRole(memberToChangeRole, newRole)}
                loading={updateMemberRoleMutation.isPending}
              >
                Update Role
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Transfer Ownership Dialog */}
      {showTransferDialog && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <Card className="p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Transfer Ownership</h3>
            <p className="text-text-secondary mb-4">
              Select a leader to become the new club owner:
            </p>
            
            <div className="space-y-2 mb-6">
              {adminMembers.map((admin) => (
                <label key={admin.user_id} className="flex items-center gap-3 p-3 rounded-lg border border-oro-border hover:bg-oro-surface-alt/30 cursor-pointer">
                  <input
                    type="radio"
                    name="newOwner"
                    value={admin.user_id}
                    checked={selectedNewOwner === admin.user_id}
                    onChange={(e) => setSelectedNewOwner(e.target.value)}
                    className="text-accent"
                  />
                  <div>
                    <p className="font-medium">{admin.public_display_name}</p>
                    <p className="text-sm text-text-muted">{admin.email}</p>
                  </div>
                </label>
              ))}
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowTransferDialog(false);
                  setSelectedNewOwner('');
                }}
                disabled={transferOwnershipMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleTransferOwnership}
                loading={transferOwnershipMutation.isPending}
                disabled={!selectedNewOwner}
                className="bg-amber-600 hover:bg-amber-700"
              >
                Transfer Ownership
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Delete Club Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <Card className="p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4 text-red-400">Delete Club</h3>
            <p className="text-text-secondary mb-4">
              Are you absolutely sure? This action cannot be undone.
            </p>
            <p className="text-sm text-text-muted mb-6">
              Type <strong>{club.name}</strong> to confirm:
            </p>
            <Input
              placeholder={club.name}
              className="mb-6"
            />
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleteClubMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleDeleteClub}
                loading={deleteClubMutation.isPending}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete Forever
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}